import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase, Grid, InputAdornment, TextField, Typography } from '@material-ui/core';
import GuideMessageM from '../components/message/GuideMessageM';
import { ButtonClearInput, CustomButton } from '../components/button';
import { AddressSearchDialog } from '../components/address';
import { orderCalAPI } from '../api/service';
import tokens from '../components/theme/tokens';

// 운임 조회(기업회원) — 출발·도착 주소를 고르면 차량별 예상 운임을 한 번에 보여 준다(거래처 운임·할인률 반영). 접수 전 미리 확인용
const VEHICLES = [
  { key: 'Bike', label: '오토', group: 'quick' },
  { key: 'Damas', label: '다마스', group: 'quick' },
  { key: 'Labo', label: '라보', group: 'quick' },
  { key: 'OntT', label: '트럭 1톤', group: 'cargo' },
  { key: 'OntPointFourT', label: '트럭 1.4톤', group: 'cargo' },
  { key: 'TwoPointFiveT', label: '트럭 2.5톤', group: 'cargo' },
  { key: 'ThreePointFiveT', label: '트럭 3.5톤', group: 'cargo' },
  { key: 'FiveT', label: '트럭 5톤', group: 'cargo' },
];

const useStyles = makeStyles(() => ({
  title: { marginBottom: 50 },
  box: {
    width: 560,
    maxWidth: '100%',
    padding: '0 30px',
    '& .MuiInputBase-root': {
      boxShadow: 'none', marginBottom: 12, width: '100%', padding: '0 16px', borderRadius: tokens.radius,
      border: `1px solid ${tokens.line}`, backgroundColor: tokens.surface,
      '& input': { height: 50, cursor: 'pointer', '&::-webkit-input-placeholder': { color: tokens.placeholder } },
      '& .MuiOutlinedInput-input': { padding: 0 },
    },
    '& .Mui-disabled': { borderColor: 'transparent !important', color: tokens.ink },
    '& .MuiOutlinedInput-notchedOutline': { borderRadius: `${tokens.radius}px !important` },
  },
  options: { display: 'flex', gap: 8, marginBottom: 14 },
  opt: {
    height: 36, padding: '0 14px', borderRadius: 18, border: `1px solid ${tokens.line}`, backgroundColor: tokens.surface, fontSize: 14, color: tokens.text,
    '&:hover': { borderColor: tokens.primary },
  },
  optOn: { borderColor: tokens.primary, backgroundColor: tokens.primarySoft, color: tokens.primaryDark, fontWeight: 700 },
  table: {
    width: '100%',
    marginTop: 20,
    borderRadius: 14,
    backgroundColor: tokens.surface,
    boxShadow: tokens.cardShadow,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: `1px solid ${tokens.lineSoft}`,
    '&:last-child': { borderBottom: 'none' },
    '& b': { flex: '1 1 auto', fontSize: 15, color: tokens.ink, letterSpacing: -0.6 },
    '& em': { fontStyle: 'normal', fontSize: 12, color: tokens.muted, marginRight: 10 },
    '& strong': { fontSize: 17, color: tokens.primaryDark, letterSpacing: -0.8 },
  },
  group: { padding: '10px 18px 6px', fontSize: 12, fontWeight: 700, color: tokens.muted, backgroundColor: '#fffaf8' },
  note: { marginTop: 12, fontSize: 13, color: tokens.muted, letterSpacing: -0.4 },
}));

function FareCheckPage({ loginStore }) {
  const cs = useStyles();
  const history = useHistory();
  const { userInfo } = loginStore;
  const [start, setStart] = useState({ addr: '', bcode: '' });
  const [end, setEnd] = useState({ addr: '', bcode: '' });
  const [dialog, setDialog] = useState('');
  const [route, setRoute] = useState('OneWay');
  const [express, setExpress] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let token = '';
    try { token = localStorage.getItem('qwdmtk') || ''; } catch (e) { token = ''; }
    if (!token) history.replace('/login');
  }, [history]);

  const select = (sel) => {
    if (dialog === 'start') setStart({ addr: sel.address, bcode: sel.bcode || '' });
    else setEnd({ addr: sel.address, bcode: sel.bcode || '' });
    setDialog('');
  };

  const lookup = async () => {
    if (!start.addr || !end.addr || busy) return;
    setBusy(true);
    const results = await Promise.all(VEHICLES.map(async (v) => {
      const options = {
        startAddr: start.addr, startPointCode: start.bcode, endAddr: end.addr, endPointCode: end.bcode,
        vehicleType: v.key, route, isExpress: express ? 'Y' : 'N',
        ...(v.group === 'cargo' ? { vehicleOptionType: 'Cargo', paletteCnt: '1' } : { documentCnt: '1' }),
      };
      const resp = await orderCalAPI(options);
      const d = resp && resp.status === 200 ? resp.data : null;
      return { ...v, total: d ? d.totalValue : null, payable: d ? d.isPayment === 'Y' : false, details: d ? d.orderDetails : [] };
    }));
    setRows(results);
    setBusy(false);
  };

  const discount = userInfo && userInfo.biz && userInfo.biz.discountRate;
  const groups = [['quick', '퀵 서비스'], ['cargo', '화물 서비스']];

  return (
    <Grid className="mainLayout" container direction="column" justify="center" alignItems="center">
      <AddressSearchDialog open={Boolean(dialog)} type={dialog === 'start' ? 'to' : 'from'} handleClose={() => setDialog('')} onSelect={select} />
      <Grid item className={cs.title}>
        <GuideMessageM title="운임 조회" resultTitle="운임 조회" subTitle="접수 전에 차량별 예상 운임을 확인합니다." />
      </Grid>
      <Grid className={cs.box} container direction="column">
        <TextField fullWidth disabled variant="outlined" placeholder="출발지 주소" value={start.addr} onClick={() => setDialog('start')}
          InputProps={{ endAdornment: <InputAdornment position="end"><ButtonClearInput /></InputAdornment> }} />
        <TextField fullWidth disabled variant="outlined" placeholder="도착지 주소" value={end.addr} onClick={() => setDialog('end')}
          InputProps={{ endAdornment: <InputAdornment position="end"><ButtonClearInput /></InputAdornment> }} />
        <div className={cs.options}>
          {[['OneWay', '편도'], ['RoundTrip', '왕복']].map(([k, l]) => (
            <ButtonBase key={k} className={`${cs.opt} ${route === k ? cs.optOn : ''}`} onClick={() => setRoute(k)} focusRipple={false}>{l}</ButtonBase>
          ))}
          <ButtonBase className={`${cs.opt} ${express ? cs.optOn : ''}`} onClick={() => setExpress((v) => !v)} focusRipple={false}>급송</ButtonBase>
        </div>
        <CustomButton fullWidth variant="contained" color="primary" disabled={!start.addr || !end.addr || busy} onClick={lookup}>
          {busy ? '조회 중' : '운임 조회'}
        </CustomButton>
        {rows.length > 0 && (
          <div className={cs.table}>
            {groups.map(([g, label]) => (
              <React.Fragment key={g}>
                <div className={cs.group}>{label}</div>
                {rows.filter((r) => r.group === g).map((r) => (
                  <div key={r.key} className={cs.row}>
                    <b>{r.label}</b>
                    {!r.payable && <em>상담 후 확정</em>}
                    <strong>{r.total ? `${Number(r.total).toLocaleString('ko-KR')}원` : '-'}</strong>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        )}
        {rows.length > 0 && (
          <Typography className={cs.note}>
            {discount ? `거래처 할인 ${discount}% 적용 · ` : ''}예상 운임이며 실제 운임은 접수 뒤 확정됩니다.
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}

export default inject('loginStore')(observer(FareCheckPage));
